import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup }            from '@angular/forms';

import { Observable, Subject }                           from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { ThemeFilters }         from '@models/themes/theme-filters';
import { ThemeFiltersCategory } from '@models/themes/theme-filters-category';
import { ThemeFiltersGenre }    from '@models/themes/theme-filters-genre';
import { GetThemesParams }      from '@models/themes/get-themes-params';
import { themeTypes }           from '@models/themes/theme-types';
import { Breakpoints }          from '@utils/breakpoints/breakpoints.enum';

import { BreakpointsService } from '@utils/breakpoints/breakpoints.service';
import { ApiThemesService }   from '@services/api-themes.service';


@Component({
  selector:    'red-theme-filters',
  templateUrl: './theme-filters.component.html',
  styleUrls:   [ './theme-filters.component.scss' ]
})
export class ThemeFiltersComponent implements OnInit, OnDestroy {

  private readonly unsubscribe$: Subject<void>;

  public readonly isMobile$: Observable<boolean>;

  private _filterByActive: boolean;
  private _filterByVideo: boolean;

  public get filterByActive() { return this._filterByActive; }

  public set filterByActive(value: boolean) {
    this._filterByActive = value;
    this.setFilterByActive(value);
  }

  public get filterByVideo() { return this._filterByVideo; }

  public set filterByVideo(value: boolean) {
    this._filterByVideo = value;
    this.setFilterByVideo(value);
  }

  public filterForm: FormGroup;

  @Input() public activeOnly:      boolean;
  @Input() public schoolId:        number;
  @Input() public fixedCategoryId: number;
  @Input() public expanded:        boolean; 

  @Output() public filtersChanged: EventEmitter<GetThemesParams>;

  constructor(
    private formBuilder:        FormBuilder,
    private breakpointsService: BreakpointsService,
    private themesService:      ApiThemesService
  ) {
    this.filtersChanged = new EventEmitter<GetThemesParams>();
    this.unsubscribe$   = new Subject<void>();
    this.isMobile$      = this.breakpointsService.observer([ Breakpoints.TabletPortraitDown ]);

    this.fixedCategoryId = null;
    this.schoolId        = null;
    this.activeOnly      = false;
    this.expanded        = true; 
  }

  ngOnInit(): void {
    this.initForm();
  }

  private initForm() {
    this.filterForm = this.formBuilder.group({});

    this.updateFiltersForm({ escolaId: this.schoolId, ativo: this.activeOnly });
  }

  public updateFiltersForm(params: GetThemesParams = {}) {
    this.themesService.getThemeFilters(params).subscribe((filters: ThemeFilters) => {
      this.buildFiltersForm(filters);
    });
  }

  private buildFiltersForm(filters: ThemeFilters) {
    this.filterForm.addControl('texto', this.formBuilder.control(''));

    this.filterForm.addControl(
      'listCategoria',
      this.buildFormArray(filters.listCategoria)
    );

    if (typeof this.fixedCategoryId === 'number') {
      const findCategoryById = (category: ThemeFiltersCategory) => category.id === this.fixedCategoryId,
            catIndex: number = filters.listCategoria.findIndex(findCategoryById);

      if (this.categoriesFilterForm.at(catIndex)) {
        this.categoriesFilterForm.at(catIndex).get('value').patchValue(true);
        this.categoriesFilterForm.disable({ onlySelf: true, emitEvent: false });
      }
    }

    this.filterForm.addControl(
      'listGenero',
      this. buildFormArray(filters.listGenero)
    );

    if (!this.activeOnly) {
      this.filterForm.addControl('ativo', this.formBuilder.control(null));

      this.filterForm.get('ativo').valueChanges.pipe(
        debounceTime(1000),
        distinctUntilChanged(),
        takeUntil(this.unsubscribe$)
      ).subscribe(() => this.applyFilters());
    }

    this.filterForm.addControl('tipoTema', this.formBuilder.control(null));

    this.typeFilterCtrl.valueChanges.pipe(
      debounceTime(1000),
      distinctUntilChanged(),
      takeUntil(this.unsubscribe$)
    ).subscribe(() => this.applyFilters());

    this.filterForm.addControl('video', this.formBuilder.control(null));

    this.filterForm.get('video').valueChanges.pipe(
      debounceTime(1000),
      distinctUntilChanged(),
      takeUntil(this.unsubscribe$)
    ).subscribe(() => this.applyFilters());
  }

  private setFilterByActive(value: boolean) {
    if (!this.filterForm || !this.filterForm.get('ativo')) return;
    if (!value) this.filterForm.get('ativo').patchValue(null);
  }

  private setFilterByVideo(value: boolean) {
    if (!this.filterForm || !this.filterForm.get('video')) return;
    if (!value) this.filterForm.get('video').patchValue(null);
  }

  private buildFormArray(
    list: ThemeFiltersCategory[] | ThemeFiltersGenre[]
  ): FormArray {
    if (!list || list.length < 1) return;

    const formArray: FormArray = new FormArray([]);

    list.forEach((item: ThemeFiltersCategory | ThemeFiltersGenre) => {
      const label: string = (item as ThemeFiltersGenre).nome || (item as ThemeFiltersCategory).nomCategoria;

      const itemControl: FormGroup = this.formBuilder.group({
        id:          this.formBuilder.control(item.id),
        value:       this.formBuilder.control(false),
        label:       this.formBuilder.control(label),
        numberLabel: this.formBuilder.control(item.qtdTema)
      });

      formArray.push(itemControl);
    });

    this.initFormArrayEvents(formArray);

    return formArray;
  }

  private initFormArrayEvents(formArray: FormArray) {
    const formChangedDetection = (a: any, b: any): boolean => JSON.stringify(a) === JSON.stringify(b);

    formArray.valueChanges.pipe(
      debounceTime(1000),
      distinctUntilChanged(formChangedDetection),
      takeUntil(this.unsubscribe$)
    ).subscribe(() => this.applyFilters());
  }

  public get termFilterForm()       { return this.filterForm?.get('texto') as FormControl; }
  public get categoriesFilterForm() { return this.filterForm?.get('listCategoria') as FormArray; }
  public get genresFilterForm()     { return this.filterForm?.get('listGenero') as FormArray; }
  public get typeFilterCtrl()       { return this.filterForm?.get('tipoTema') as FormControl; }

  private get typeFilter(): themeTypes { return this.typeFilterCtrl?.value; }

  private get activeFilter(): boolean | null {
    if (this.activeOnly) return true;
    return this.filterByActive ? this.filterForm.get('ativo').value : null;
  }

  private get videoFilter(): boolean | null {
    return this.filterByVideo ? this.filterForm.get('video').value : null;
  }

  public getSelectedFilterIds(formArray: FormArray): number[] {
    if (!formArray) return [];

    const filterSelected = (item: any): boolean => item.value === true,
          getId          = (item: any): number => item.id;

    return formArray.value.filter(filterSelected).map(getId);
  }

  public getAppliedFilters(): GetThemesParams {
    const selectedCategories: number[] = this.getSelectedFilterIds(this.categoriesFilterForm),
          selectedGenres: number[]     = this.getSelectedFilterIds(this.genresFilterForm),
          searchTerm: string           = this.termFilterForm?.value || null;

    return {
      tipoTema:      this.typeFilter,
      ativo:         this.activeFilter,
      video:         this.videoFilter,
      texto:         searchTerm,
      escolaId:      typeof this.schoolId === 'number' ? this.schoolId : null,
      listCategoria: selectedCategories?.length ? selectedCategories : null,
      listGenero:    selectedGenres?.length ? selectedGenres : null
    };
  }

  public applyFilters() {
    this.filtersChanged.emit(this.getAppliedFilters());
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
